export type Locale = 'tr' | 'en';

type L = { tr: string; en: string };

export type AuthorityAssetFaqItem = {
  question: L;
  answer: L;
};

export type AuthorityAssetSection = {
  title: L;
  bullets: L[];
};

export type AuthorityAsset = {
  slug: string;
  title: L;
  seoTitle: L;
  seoDescription: L;
  heroTitle: L;
  summary: L;
  durableValue: L;
  scopeNote: L;
  sections: AuthorityAssetSection[];
  relatedServiceSlugs: string[];
  relatedSolutionSlugs: string[];
  relatedProjectSlugs: string[];
  relatedBlogSlugs: string[];
  relatedToolIds: string[];
  faqs: AuthorityAssetFaqItem[];
};

export function localizeAuthorityAssetText(value: L, lang: Locale) {
  return value[lang];
}

const assets: AuthorityAsset[] = [
  {
    slug: 'ai-assisted-product-development-approach',
    title: {
      tr: 'AI-Assisted Product Development Yaklaşımımız',
      en: 'How We Approach AI-Assisted Product Development',
    },
    seoTitle: {
      tr: 'AI Destekli Ürün Geliştirme Yaklaşımımız',
      en: 'How We Approach AI-Assisted Product Development',
    },
    seoDescription: {
      tr: 'Figtures\'ın AI-enabled ürün geliştirmede discovery, mimari, sprint delivery ve kalite kontrol yaklaşımını açıklayan kalıcı playbook.',
      en: 'A durable playbook describing how Figtures runs discovery, architecture, sprint delivery, and quality control for AI-enabled product work.',
    },
    heroTitle: {
      tr: 'AI destekli ürünleri, hızlı ama kontrol edilebilir şekilde nasıl teslim ediyoruz?',
      en: 'How do we deliver AI-assisted products fast without losing control?',
    },
    summary: {
      tr: 'Bu playbook, AI özelliklerinin ürün değerine bağlanması için kullandığımız karar çerçevesini açıklar.',
      en: 'This playbook explains the decision framework we use to tie AI features to product value.',
    },
    durableValue: {
      tr: 'Zamanla değişmeyen karar kalıplarını içerir: hangi use-case\'in gerçekten ürün değeri ürettiği, nasıl güvenli rollout yapıldığı ve ekip içinde kalite standardının nasıl korunduğu.',
      en: 'It captures stable decision patterns: which use cases create product value, how to roll out safely, and how quality standards are maintained within the team.',
    },
    scopeNote: {
      tr: 'Not: Bu içerik bir ürün teslimat playbook\'udur; model sağlayıcılara resmi bağlılık iddiası içermez.',
      en: 'Note: This is a delivery playbook and does not claim official affiliation with model providers.',
    },
    sections: [
      {
        title: { tr: 'Ne Teslim Ederiz', en: 'What We Typically Deliver' },
        bullets: [
          {
            tr: 'Ürün içinde çalışan AI özellikleri (asistan, özetleme, sınıflandırma, öneri).',
            en: 'Production AI features inside the product (assistant, summarization, classification, recommendation).',
          },
          {
            tr: 'Model orkestrasyonu, fallback stratejileri ve gözlemlenebilirlik katmanı.',
            en: 'Model orchestration, fallback strategies, and observability.',
          },
          {
            tr: 'Prompt kalite kontrolü, eval yaklaşımı ve release guardrail seti.',
            en: 'Prompt quality control, evaluation approach, and release guardrails.',
          },
        ],
      },
      {
        title: { tr: 'Engagement Şekilleri', en: 'Typical Engagement Shapes' },
        bullets: [
          {
            tr: 'Discovery + ilk production sprint: kritik use-case\'in canlıya alınması.',
            en: 'Discovery + first production sprint: shipping the highest-impact use case.',
          },
          {
            tr: 'Mevcut ürün ekibine embedded AI execution desteği.',
            en: 'Embedded AI execution support for existing product teams.',
          },
          {
            tr: 'Post-launch kalite ve maliyet optimizasyon sprintleri.',
            en: 'Post-launch quality and cost optimization sprints.',
          },
        ],
      },
      {
        title: { tr: 'Optimize Ettiğimiz Sinyaller', en: 'Signals We Optimize For' },
        bullets: [
          {
            tr: 'Kullanıcı değeri: AI çıktısının gerçek akışta karar/aksiyon üretmesi.',
            en: 'User value: AI output must drive decisions/actions in real workflows.',
          },
          {
            tr: 'Operasyonel güvenilirlik: hata durumunda kontrollü fallback davranışı.',
            en: 'Operational reliability: controlled fallback behavior on failure.',
          },
          {
            tr: 'Takım sürdürülebilirliği: bakım yapılabilir kod ve dokümante süreçler.',
            en: 'Team sustainability: maintainable code and documented processes.',
          },
        ],
      },
    ],
    relatedServiceSlugs: ['ai-development', 'custom-software-development', 'startup-product-engineering'],
    relatedSolutionSlugs: ['chatgpt-integration-development', 'claude-automation-solutions'],
    relatedProjectSlugs: ['ztech', 'lcw'],
    relatedBlogSlugs: ['evaluation-debt-in-ai-product-features', 'developer-experience-as-architecture'],
    relatedToolIds: ['project-cost-estimator', 'customer-reply-generator'],
    faqs: [
      {
        question: {
          tr: 'AI özelliklerini ne zaman ürün roadmap\'ine almalı?',
          en: 'When should AI features enter a product roadmap?',
        },
        answer: {
          tr: 'Kullanıcı problemi netse ve AI çıktısı ürün içindeki bir kararı/aksiyonu iyileştiriyorsa. Sadece trend olduğu için eklemekten kaçınıyoruz.',
          en: 'When the user problem is clear and AI output improves a real decision/action in-product. We avoid adding AI only because it is trendy.',
        },
      },
      {
        question: {
          tr: 'PoC\'den production\'a geçişte en kritik adım ne?',
          en: 'What is most critical when moving from PoC to production?',
        },
        answer: {
          tr: 'Eval, fallback ve observability katmanını ürün akışıyla birlikte tasarlamak. Bu olmadan kalite sürdürülemez.',
          en: 'Designing evaluation, fallback, and observability with the product workflow. Without this, quality does not scale.',
        },
      },
    ],
  },
  {
    slug: 'chatgpt-vs-claude-workflow-selection',
    title: {
      tr: 'ChatGPT vs Claude: Ürün Workflow\'larında Seçim Kılavuzu',
      en: 'ChatGPT vs Claude for Product Workflows: Selection Guide',
    },
    seoTitle: {
      tr: 'ChatGPT vs Claude: Ürün Workflow Seçim Kılavuzu',
      en: 'ChatGPT vs Claude Workflow Selection Guide',
    },
    seoDescription: {
      tr: 'ChatGPT ve Claude\'u ürün, destek ve operasyon workflow\'larında hangi karar kriterleriyle seçtiğimizi anlatan uygulamalı kılavuz.',
      en: 'A practical guide explaining how we choose between ChatGPT and Claude for product, support, and operations workflows.',
    },
    heroTitle: {
      tr: 'ChatGPT mi Claude mu? Kararı marka adına göre değil, iş akışına göre veriyoruz.',
      en: 'ChatGPT or Claude? We decide by workflow fit, not brand preference.',
    },
    summary: {
      tr: 'Bu varlık, model seçim kararını teknik ve ürün sinyallerine bağlar.',
      en: 'This asset ties model-selection decisions to technical and product signals.',
    },
    durableValue: {
      tr: 'Belirli bir model sürümünden bağımsız, workflow odaklı seçim çerçevesi sunar.',
      en: 'It provides a workflow-first selection framework independent of specific model versions.',
    },
    scopeNote: {
      tr: 'Not: Bu içerik tarafsız uygulama deneyimine dayanır; OpenAI veya Anthropic ile resmi bağlılık iddiası içermez.',
      en: 'Note: This content reflects implementation practice and does not imply official affiliation with OpenAI or Anthropic.',
    },
    sections: [
      {
        title: { tr: 'Karar Kriterleri', en: 'Decision Criteria' },
        bullets: [
          {
            tr: 'Görev tipi: yaratıcı üretim, belge analizi, karar desteği veya araç orkestrasyonu.',
            en: 'Task type: creative generation, document analysis, decision support, or tool orchestration.',
          },
          {
            tr: 'Risk seviyesi: yanlış cevabın operasyonel etkisi ve fallback zorunluluğu.',
            en: 'Risk level: operational impact of incorrect responses and fallback requirements.',
          },
          {
            tr: 'Ürün bağlamı: yanıt kalitesinin UX ve dönüşüm akışına etkisi.',
            en: 'Product context: impact of response quality on UX and conversion flow.',
          },
        ],
      },
      {
        title: { tr: 'Bu Çalışma Genelde Neleri İçerir', en: 'What This Work Usually Includes' },
        bullets: [
          { tr: 'Prompt/eval matrisi ve test senaryoları.', en: 'Prompt/evaluation matrix and test scenarios.' },
          { tr: 'Workflow başına model seçimi ve fallback kuralları.', en: 'Per-workflow model selection and fallback rules.' },
          { tr: 'Release sonrası kalite izleme ve maliyet dengeleme.', en: 'Post-release quality monitoring and cost balancing.' },
        ],
      },
      {
        title: { tr: 'İyi Uyum / Uygun Değil', en: 'Good Fit / Not a Fit' },
        bullets: [
          {
            tr: 'İyi uyum: gerçek ürün use-case\'i olan ve kalite hedefi tanımlı ekipler.',
            en: 'Good fit: teams with real product use cases and explicit quality targets.',
          },
          {
            tr: 'Uygun değil: yalnızca marka adı üzerinden model seçmek isteyen projeler.',
            en: 'Not a fit: projects selecting models based purely on brand names.',
          },
        ],
      },
    ],
    relatedServiceSlugs: ['ai-development', 'ai-automation-agents'],
    relatedSolutionSlugs: ['chatgpt-integration-development', 'claude-automation-solutions'],
    relatedProjectSlugs: ['lcw'],
    relatedBlogSlugs: ['evaluation-debt-in-ai-product-features'],
    relatedToolIds: ['customer-reply-generator'],
    faqs: [
      {
        question: { tr: 'Tek ürün içinde birden fazla model kullanılır mı?', en: 'Can one product use multiple models?' },
        answer: {
          tr: 'Evet. Farklı workflow\'larda farklı model kullanımı çoğu zaman daha doğru sonuç verir.',
          en: 'Yes. Different workflows often benefit from different model choices.',
        },
      },
      {
        question: { tr: 'Model seçimi bir kere yapılıp bitiyor mu?', en: 'Is model selection a one-time decision?' },
        answer: {
          tr: 'Hayır. Ürün sinyallerine göre periyodik olarak yeniden değerlendiriyoruz.',
          en: 'No. It is re-evaluated periodically based on product signals.',
        },
      },
    ],
  },
  {
    slug: 'cursor-codex-workflow-acceleration-guide',
    title: {
      tr: 'Cursor/Codex Workflow Hızlandırma Rehberi',
      en: 'Cursor/Codex Workflow Acceleration Guide',
    },
    seoTitle: {
      tr: 'Cursor/Codex Workflow Hızlandırma Rehberi',
      en: 'How Cursor/Codex Workflows Accelerate Engineering Teams',
    },
    seoDescription: {
      tr: 'AI coding araçlarını takım standardı, review kalitesi ve release güvenliğiyle birlikte kullanma rehberi.',
      en: 'A guide for using AI coding tools with team standards, review quality, and release safety.',
    },
    heroTitle: {
      tr: 'AI coding araçlarıyla hız kazanırken kaliteyi nasıl koruyoruz?',
      en: 'How we increase speed with AI coding tools while protecting quality',
    },
    summary: {
      tr: 'Kod üretim hızını artırırken hata maliyetini kontrol altında tutan mühendislik yaklaşımı.',
      en: 'An engineering approach that increases coding velocity while controlling defect cost.',
    },
    durableValue: {
      tr: 'Araç bağımsız süreç ilkeleri içerir: prompt disiplini, review sınırları, test kapıları ve rollout güvenliği.',
      en: 'It captures tool-agnostic process principles: prompt discipline, review boundaries, testing gates, and rollout safety.',
    },
    scopeNote: {
      tr: 'Not: Cursor/Codex isimleri teknik bağlam içindir; resmi temsil iddiası içermez.',
      en: 'Note: Cursor/Codex references are technical context only and do not imply official representation.',
    },
    sections: [
      {
        title: { tr: 'Optimize Ettiğimiz Sinyaller', en: 'Signals We Optimize For' },
        bullets: [
          { tr: 'Lead time kısalması', en: 'Shorter lead time' },
          { tr: 'Review kalitesi ve tutarlılığı', en: 'Review quality and consistency' },
          { tr: 'Regression riskinin kontrolü', en: 'Controlled regression risk' },
        ],
      },
      {
        title: { tr: 'Tipik Engagement Şekli', en: 'Typical Engagement Shape' },
        bullets: [
          { tr: 'Workflow audit + pilot team kurulumu', en: 'Workflow audit + pilot team setup' },
          { tr: 'Prompt/review policy standardizasyonu', en: 'Prompt/review policy standardization' },
          { tr: 'CI quality gate ve release guardrail uyarlaması', en: 'CI quality gate and release guardrail adaptation' },
        ],
      },
      {
        title: { tr: 'Ne Zaman Uygun Değil', en: 'When It Is Not the Right Fit' },
        bullets: [
          {
            tr: 'Kod review kültürü olmayan ve test disiplini kurmak istemeyen ekipler.',
            en: 'Teams without code review culture and no commitment to testing discipline.',
          },
          {
            tr: 'Hız beklentisi olup kalite kontrol mekanizması istemeyen projeler.',
            en: 'Projects demanding speed while refusing quality-control mechanisms.',
          },
        ],
      },
    ],
    relatedServiceSlugs: ['custom-software-development', 'startup-product-engineering'],
    relatedSolutionSlugs: ['cursor-codex-engineering-workflows'],
    relatedProjectSlugs: ['lcw', 'ailem-aselsan'],
    relatedBlogSlugs: ['developer-experience-as-architecture', 'release-geometry-progressive-risk-slicing'],
    relatedToolIds: ['project-cost-estimator', 'customer-reply-generator'],
    faqs: [
      {
        question: { tr: 'AI coding araçları junior ekiplerde riskli mi?', en: 'Are AI coding tools risky for junior-heavy teams?' },
        answer: {
          tr: 'Doğru review ve test kapılarıyla yönetilebilir. Sorun araç değil, kontrolsüz kullanım modelidir.',
          en: 'They are manageable with proper review and testing gates. The risk is uncontrolled usage, not the tools themselves.',
        },
      },
      {
        question: { tr: 'Bu yaklaşım mevcut repo\'ya sonradan uygulanabilir mi?', en: 'Can this approach be applied to an existing codebase?' },
        answer: {
          tr: 'Evet. Genellikle pilot bir modül/squad ile başlayıp kademeli yaygınlaştırma yapıyoruz.',
          en: 'Yes. We usually start with a pilot module/squad and scale progressively.',
        },
      },
    ],
  },
  {
    slug: 'scoping-ai-integrations-for-web-and-mobile',
    title: {
      tr: 'Web ve Mobil Ürünler İçin AI Entegrasyon Scope Rehberi',
      en: 'How We Scope AI Integrations for Web and Mobile Products',
    },
    seoTitle: {
      tr: 'Web ve Mobil Ürünler İçin AI Entegrasyon Scope Rehberi',
      en: 'How We Scope AI Integrations for Web and Mobile Products',
    },
    seoDescription: {
      tr: 'AI entegrasyon projelerinde kapsamı doğru belirlemek için kullandığımız ürün, teknik ve delivery çerçevesi.',
      en: 'Our product, technical, and delivery framework for scoping AI integrations in web and mobile products.',
    },
    heroTitle: {
      tr: 'AI entegrasyonunda kapsamı doğru kurmak, teslimat hızından daha kritiktir.',
      en: 'Getting scope right in AI integrations is more critical than moving fast blindly.',
    },
    summary: {
      tr: 'Bu rehber, AI entegrasyonunda “ne yapılmalı / ne ertelenmeli” kararlarını sistematik hale getirir.',
      en: 'This guide systematizes “what to build now vs later” decisions in AI integrations.',
    },
    durableValue: {
      tr: 'Model veya framework değişse bile geçerli kalan scoping prensipleri içerir.',
      en: 'It provides scoping principles that remain valid even as models/frameworks evolve.',
    },
    scopeNote: {
      tr: 'Not: Rehberdeki öneriler ürün hedefi ve ekip kapasitesine göre uyarlanır.',
      en: 'Note: Recommendations are adapted to product goals and team capacity.',
    },
    sections: [
      {
        title: { tr: 'Scope\'a Dahil Ettiklerimiz', en: 'What We Include in Scope' },
        bullets: [
          { tr: 'En yüksek etki üreten 1-2 kritik AI kullanım senaryosu.', en: 'The 1–2 highest-impact AI use cases.' },
          { tr: 'Veri, prompt, fallback ve ürün UX entegrasyonu.', en: 'Data, prompt, fallback, and product UX integration.' },
          { tr: 'Release sonrası kalite gözlemi için temel ölçüm sinyalleri.', en: 'Core measurement signals for post-release quality tracking.' },
        ],
      },
      {
        title: { tr: 'İlk Fazda Ertelediklerimiz', en: 'What We Defer in Phase One' },
        bullets: [
          { tr: 'Düşük etki / yüksek karmaşıklık yan use-case\'ler.', en: 'Low-impact, high-complexity secondary use cases.' },
          { tr: 'Operasyon ekibi hazır değilse ileri otomasyon katmanları.', en: 'Advanced automation layers when operations are not ready.' },
          { tr: 'Kanıt üretmeyen “sadece AI görünsün” talepleri.', en: '“AI for optics” requests that do not produce evidence of value.' },
        ],
      },
      {
        title: { tr: 'Kimler İçin İyi Uyum', en: 'What Makes This a Good Fit' },
        bullets: [
          { tr: 'Ürün öncelikleri net, sprint disiplini olan ekipler.', en: 'Teams with clear product priorities and sprint discipline.' },
          { tr: 'Web/mobil ürününe AI entegrasyonunu kademeli ve ölçülebilir yapmak isteyen şirketler.', en: 'Teams wanting incremental, measurable AI integration into web/mobile products.' },
        ],
      },
    ],
    relatedServiceSlugs: ['web-development', 'mobile-app-development', 'ai-development'],
    relatedSolutionSlugs: ['ai-powered-website-development', 'ai-powered-app-development'],
    relatedProjectSlugs: ['ztech', 'ailem-aselsan'],
    relatedBlogSlugs: ['api-contract-evolution-without-breakage', 'offline-first-mobile-sync-conflict-discipline'],
    relatedToolIds: ['project-cost-estimator', 'youtube-seo-generator'],
    faqs: [
      {
        question: { tr: 'Web ve mobilde aynı anda AI entegrasyonu yapmak doğru mu?', en: 'Should web and mobile AI integrations be shipped at the same time?' },
        answer: {
          tr: 'Her zaman değil. Önce en yüksek etkiyi üreten yüzeyde doğrulama yapıp sonra diğer platforma genişletmek çoğu ekip için daha güvenlidir.',
          en: 'Not always. Validating on the highest-impact surface first and then expanding is usually safer.',
        },
      },
      {
        question: { tr: 'AI entegrasyon scope\'u neden küçük başlamalı?', en: 'Why should AI integration scope start small?' },
        answer: {
          tr: 'Erken fazda kalite ve kullanım sinyalini görmek, yanlış yatırım riskini azaltır ve sonraki sprintleri daha doğru yönlendirir.',
          en: 'Early quality and usage signals reduce misallocation risk and make later sprints more accurate.',
        },
      },
    ],
  },
];

const bySlug = new Map(assets.map((asset) => [asset.slug, asset]));

function unique(values: string[]) {
  return [...new Set(values)];
}

export function getAllAuthorityAssets() {
  return assets;
}

export function getAuthorityAssetBySlug(slug: string) {
  return bySlug.get(slug) ?? null;
}

/** Public playbook URL for a catalog slug — null when slug is unknown. */
export function getAuthorityAssetPlaybookPath(slug: string): `/playbooks/${string}` | null {
  if (!bySlug.has(slug)) return null;
  return `/playbooks/${slug}`;
}

export function getAuthorityAssetsBySlugs(slugs: string[]) {
  return slugs
    .map((slug) => bySlug.get(slug))
    .filter((item): item is AuthorityAsset => Boolean(item));
}

export function getFeaturedAuthorityAssetSlugs() {
  return [
    'ai-assisted-product-development-approach',
    'chatgpt-vs-claude-workflow-selection',
    'cursor-codex-workflow-acceleration-guide',
  ];
}

export function getRelatedAuthorityAssetSlugsForService(serviceSlug: string) {
  return unique(
    assets
      .filter((asset) => asset.relatedServiceSlugs.includes(serviceSlug))
      .map((asset) => asset.slug),
  );
}

export function getRelatedAuthorityAssetSlugsForSolution(solutionSlug: string) {
  return unique(
    assets
      .filter((asset) => asset.relatedSolutionSlugs.includes(solutionSlug))
      .map((asset) => asset.slug),
  );
}

export function getRelatedAuthorityAssetSlugsForProject(projectSlug: string) {
  return unique(
    assets
      .filter((asset) => asset.relatedProjectSlugs.includes(projectSlug))
      .map((asset) => asset.slug),
  );
}

export function getRelatedAuthorityAssetSlugsForBlog(blogSlug: string) {
  return unique(
    assets
      .filter((asset) => asset.relatedBlogSlugs.includes(blogSlug))
      .map((asset) => asset.slug),
  );
}

export function getRelatedAuthorityAssetSlugsForTool(toolId: string) {
  return unique(
    assets
      .filter((asset) => asset.relatedToolIds.includes(toolId))
      .map((asset) => asset.slug),
  );
}
